import { ElementRef, Injectable, Renderer2, RendererFactory2, RendererStyleFlags2, ViewContainerRef } from '@angular/core';
import forEach from 'lodash-es/forEach';

/**
 * @deprecated please use renderer2 from angular
 */
@Injectable({
    providedIn: 'root'
})
export class Renderer {
    protected renderer: Renderer2;

    constructor(rendererFactory: RendererFactory2) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    /**
     * Adds class to element.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {any}    element
     * @param  {string} className
     */
    addClass(element: any, className: string): void {
        this.renderer.addClass(element, className);
    }

    /**
     * Removes class from element.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {any}    element
     * @param  {string} className
     */
    removeClass(element: any, className: string): void {
        this.renderer.removeClass(element, className);
    }

    /**
     * Adds or removes class on elemenet depending on to property.
     *
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {any}    element
     * @param  {string} className
     * @param  {to} boolean
     */
    setClass(element: any, className: string | string[], to: boolean): void {
        if (!element) {
            return;
        }
        if (to) {
            if (typeof className === 'string') {
                this.addClass(element, className);
            } else if (Array.isArray(className)) {
                forEach(className, (name: string) => this.addClass(element, name));
            }
        } else {
            if (typeof className === 'string') {
                this.removeClass(element, className);
            } else if (Array.isArray(className)) {
                forEach(className, (name: string) => this.removeClass(element, name));
            }
        }
    }

    /**
     * Check if element contains class name.
     *
     * @author Rafał Wolak <rafal@tomandco.co.uk>
     * @param {(ElementRef | ViewContainerRef)} element
     * @param {string} className
     * @returns {boolean}
     * @memberof Renderer
     */
    containsClass(el: ElementRef | ViewContainerRef, className: string): boolean {
        if (el instanceof ElementRef) {
            return el.nativeElement.classList.contains(className);
        } else if (el instanceof ViewContainerRef) {
            return el.element.nativeElement.classList.contains(className);
        }

        return false;
    }

    /**
     * Add attribute to the element.
     *
     * @param {*} el
     * @param {string} name
     * @param {string} value
     * @memberof Renderer
     */
    setAttribute(el: any, name: string, value: string) {
        this.renderer.setAttribute(el, name, value);
    }

    /**
     * Ramove attribute from the element.
     *
     * @param {*} el
     * @param {string} name
     * @memberof Renderer
     */
    removeAttribute(el: any, name: string) {
        this.renderer.removeAttribute(el, name);
    }

    /**
     * Remove element.
     *
     * @param {(ElementRef | ViewContainerRef)} el
     * @memberof Renderer
     */
    remove(el: ElementRef | ViewContainerRef | HTMLElement) {
        if (el instanceof ElementRef) {
            el.nativeElement.remove();
        } else if (el instanceof ViewContainerRef) {
            el.element.nativeElement.remove();
        } else {
            el.remove();
        }
    }

    createElement(name: string, namespace?: string) {
        return this.renderer.createElement(name, namespace);
    }

    removeElement(element: HTMLElement): void {
        this.renderer.removeChild(element.parentElement, element);
    }

    /**
     * Implement this callback to set a CSS style for an element in the DOM.
     *
     * @author Rafał Wolak <rafal@tomandco.co.uk>
     * @param {*} el The element.
     * @param {string} style The name of the style.
     * @param {any} value The new value.
     * @param {RendererStyleFlags2} [flags] Flags for style variations. No flags are set by default.
     * @memberof Renderer
     */
    setStyle(el: any, style: string, value: any, flags?: RendererStyleFlags2): void {
        this.renderer.setStyle(el, style, value, flags);
    }

    removeStyle(el: any, style: string, flags?: RendererStyleFlags2): void {
        this.renderer.removeStyle(el, style, flags);
    }

    /**
     * Implement this callback to set the value of a property of an element in the DOM.
     *
     * @author Rafał Wolak <rafal@tomandco.co.uk>
     * @param {*} el The element.
     * @param {string} name The property name.
     * @param {string} value The new value.
     * @memberof Renderer
     */
    setProperty(el: any, name: string, value: any): void {
        this.renderer.setProperty(el, name, value);
    }

    /**
     * Implement this callback to start an event listener.
     * @param target The context in which to listen for events. Can be
     * the entire window or document, the body of the document, or a specific
     * DOM element.
     * @param eventName The event to listen for.
     * @param callback A handler function to invoke when the event occurs.
     * @returns An "unlisten" function for disposing of this handler.
     */
    listen(target: 'window' | 'document' | 'body' | any, eventName: string, callback: (event: any) => boolean | void) {
        this.renderer.listen(target, eventName, callback);
    }
}
